import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box, Typography, Avatar, LinearProgress, Paper } from "@mui/material";
import { useParams } from "react-router-dom";
import axios from "axios";
import ChatWindow from "../components/ChatWindow";

export default function MomentPage({ sessionId: propSessionId }) {
  // ✅ Keep params only as a fallback (harmless in overlay mode)
  const { id: routeSessionId } = useParams();
  const sessionId = propSessionId || routeSessionId;

  const currentUser = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("user")) || {};
    } catch {
      return {};
    }
  }, []);

  const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:5000";

  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);

  const [timeLeft, setTimeLeft] = useState("30:00");
  const [progress, setProgress] = useState(100);

  const [showCloseSheet, setShowCloseSheet] = useState(false);
  const [leftMoment, setLeftMoment] = useState(false);

  const [expiresAtMs, setExpiresAtMs] = useState(null);

  // Prevent double end/leave
  const endingRef = useRef(false);

    const endMoment = React.useCallback(
  async (source = "manual") => {
    if (!sessionId) return;
    if (endingRef.current) return;
    if (leftMoment) return;

    endingRef.current = true;
    setLeftMoment(true);

    try {
      await axios.post(`${API_BASE}/connect/end/${sessionId}`, { confirm: true });
      sessionStorage.removeItem("active_moment_id");
    } catch (err) {
      console.error("End moment failed:", err);
    }

    sessionStorage.setItem("moment_suppressed", "1");

    window.dispatchEvent(new Event("moment-suppress"));
    window.dispatchEvent(new Event("moment-ended"));
    sessionStorage.setItem("go_home_pending", "1");
    window.dispatchEvent(new Event("go-home"));
  },
  [sessionId, API_BASE, leftMoment]
);


 const universalExit = React.useCallback(() => {
  sessionStorage.removeItem("active_moment_id");
  sessionStorage.setItem("moment_suppressed", "1");

  window.dispatchEvent(new Event("moment-ended"));
  window.dispatchEvent(new Event("go-home"));
}, []);


  /* ============================
     Load moment session
  ============================ */
  useEffect(() => {
    if (!sessionId || leftMoment) return;

    let cancelled = false;

    const loadSession = async () => {
      try {
        setLoading(true);

        const res = await axios.get(`${API_BASE}/connect/${sessionId}`);
        if (cancelled) return;

        const s = res.data?.session;

        // If backend returns null, moment is ended/expired
        if (!s) {
          universalExit();
          return;
        }

        setSession(s);

        const expires =
          s.expiresAt ||
          (s.createdAt
            ? new Date(new Date(s.createdAt).getTime() + 30 * 60 * 1000).toISOString()
            : null);

        if (!expires) {
          universalExit();
          return;
        }

        setExpiresAtMs(new Date(expires).getTime());
      } catch (err) {
        if (!cancelled) {
          console.error("Moment load error:", err);
          universalExit();
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadSession();

    return () => {
      cancelled = true;
    };
    }, [sessionId, API_BASE, leftMoment, universalExit]);

 

  /* ============================
     Presence join / leave
  ============================ */
  useEffect(() => {
    if (!sessionId || !currentUser?.id || leftMoment) return;

    axios
      .post(`${API_BASE}/moments/${sessionId}/join`, { userId: currentUser.id })
      .catch(() => {});

    return () => {
      axios
        .post(`${API_BASE}/moments/${sessionId}/leave`, { userId: currentUser.id })
        .catch(() => {});
    };
  }, [sessionId, currentUser?.id, API_BASE, leftMoment]);

  /* ============================
     Local countdown timer
  ============================ */
  useEffect(() => {
    if (!expiresAtMs || leftMoment) return;

    const MAX_MS = 30 * 60 * 1000;

    const tick = () => {
      const remainingMs = expiresAtMs - Date.now();

      if (remainingMs <= 0) {
        setTimeLeft("0:00");
        setProgress(0);
        endMoment("timer");
        return;
      }

      const remainingSec = Math.floor(remainingMs / 1000);
      const min = Math.floor(remainingSec / 60);
      const sec = remainingSec % 60;

      setTimeLeft(`${min}:${sec < 10 ? "0" : ""}${sec}`);

      const elapsedMs = MAX_MS - remainingMs;
      const pct = 100 - (elapsedMs / MAX_MS) * 100;
      setProgress(Math.max(0, Math.min(100, pct)));
    };

    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
 }, [expiresAtMs, leftMoment, endMoment]);


  /* ============================
     Optional: server re-check
  ============================ */
  useEffect(() => {
    if (!sessionId || leftMoment) return;

    const interval = setInterval(async () => {
      try {
        const res = await axios.get(`${API_BASE}/connect/${sessionId}`);
        const s = res.data?.session;
        if (!s) universalExit();
      } catch {
        // ignore transient errors
      }
    }, 10000);

    return () => clearInterval(interval);
 }, [sessionId, API_BASE, leftMoment, universalExit]);


  /* ============================
     Leave moment
     ✅ MUST suppress so wrapper doesn't reopen
  ============================ */
  const handleLeaveMoment = () => {
    if (leftMoment) return;

    setLeftMoment(true);

    axios
      .post(`${API_BASE}/moments/${sessionId}/leave`, { userId: currentUser.id })
      .catch(() => {});

    const activeId = sessionStorage.getItem("active_moment_id") || sessionId;
    sessionStorage.setItem(`moment_left_${activeId}`, "1");

    // ✅ suppress on leave too
    sessionStorage.setItem("moment_suppressed", "1");

    window.dispatchEvent(
      new CustomEvent("moment-left", { detail: { sessionId: activeId } })
    );

    setShowCloseSheet(false);

    // ✅ close overlay + go back
    window.dispatchEvent(new Event("moment-ended"));
    sessionStorage.setItem("go_home_pending", "1");
    window.dispatchEvent(new Event("go-home"));

    
  };

  /* ============================
     End moment (manual or timer)
  ============================ */


  /* ============================
     Render guards
  ============================ */
  if (!sessionId) return null;
  if (loading || !session) return null;

  /* ============================
     Resolve other user
  ============================ */
  const isUserA = Number(session.userA) === Number(currentUser.id);

  const otherUser = isUserA
    ? {
        id: session.userB,
        username: session.userBUsername,
        profilePic: session.userBProfilePic,
      }
    : {
        id: session.userA,
        username: session.userAUsername,
        profilePic: session.userAProfilePic,
      };

  return (
    <Box
      sx={{
        height: "100vh",          // ✅ ensure full height
        bgcolor: "#F9FAFB",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* ================= HEADER ================= */}
      <Paper
        elevation={0}
        sx={{
          px: 2,
          py: 1.5,
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          borderBottom: "1px solid #E5E7EB",
          borderRadius: 0,
          flexShrink: 0,
        }}
      >
        <Avatar
          src={
            otherUser.profilePic
              ? otherUser.profilePic.startsWith("http")
                ? otherUser.profilePic
                : `${API_BASE}${otherUser.profilePic}`
              : undefined
          }
          sx={{
            width: 44,
            height: 44,
            border: "1px solid #E5E7EB",
            bgcolor: "#E5E7EB",
          }}
        />

        <Box sx={{ flex: 1 }}>
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: "0.95rem",
              color: "#111827",
              lineHeight: 1.2,
            }}
          >
            @{otherUser.username}
          </Typography>

          <Typography sx={{ fontSize: "0.75rem", color: "#6B7280" }}>
            Moment active · {timeLeft}
          </Typography>
        </Box>

        <Typography
          onClick={() => setShowCloseSheet(true)}
          sx={{
            fontSize: "0.85rem",
            fontWeight: 600,
            color: "#6B7280",
            cursor: "pointer",
            userSelect: "none",
          }}
        >
          Close
        </Typography>
      </Paper>

      {/* ================= TIMER ================= */}
      <LinearProgress
        variant="determinate"
        value={progress}
        sx={{
          height: 5,
          bgcolor: "#E5E7EB",
          flexShrink: 0,
          "& .MuiLinearProgress-bar": { bgcolor: "#16A34A" },
        }}
      />

      {/* ================= CHAT AREA ================= */}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,          // ✅ critical for scroll
          display: "flex",
          flexDirection: "column",
        }}
      >
        <ChatWindow momentId={sessionId} />
      </Box>

      {/* ================= CLOSE SHEET ================= */}
      {showCloseSheet && (
        <Box
          onClick={() => setShowCloseSheet(false)}
          sx={{
            position: "fixed",
            inset: 0,
            bgcolor: "rgba(0,0,0,0.25)",
            zIndex: 1400,
          }}
        >
          <Box
            onClick={(e) => e.stopPropagation()}
            sx={{
              position: "absolute",
              bottom: 0,
              left: 0,
              right: 0,
              bgcolor: "#FFFFFF",
              borderTopLeftRadius: 20,
              borderTopRightRadius: 20,
              p: 2,
              boxShadow: "0 -8px 24px rgba(0,0,0,0.12)",
            }}
          >
            <Box
              sx={{
                width: 36,
                height: 4,
                bgcolor: "#D1D5DB",
                borderRadius: 2,
                mx: "auto",
                mb: 2,
              }}
            />

            {/* Leave */}
            <Box
              onClick={(e) => {
                e.stopPropagation();
                handleLeaveMoment();
              }}
              sx={{
                py: 1.75,
                textAlign: "center",
                fontWeight: 600,
                fontSize: "0.95rem",
                color: "#111827",
                borderBottom: "1px solid #E5E7EB",
                cursor: "pointer",
              }}
            >
              Leave moment
            </Box>

            {/* End */}
            <Box
              onClick={async () => {
                setShowCloseSheet(false);
                await endMoment("button");
              }}
              sx={{
                py: 1.75,
                textAlign: "center",
                fontWeight: 600,
                fontSize: "0.95rem",
                color: "#DC2626",
                cursor: "pointer",
              }}
            >
              End moment
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
}
